// src/contexts/WalletContext.jsx - VERSÃO ATUALIZADA
import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserProvider } from 'ethers';

const WalletContext = createContext({});

// Lista de admins
const ADMIN_ADDRESSES = [
  '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0', // Exemplo
  // Adicione seus endereços aqui
].map(addr => addr.toLowerCase());

export const WalletProvider = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [walletType, setWalletType] = useState(''); // Nova propriedade

  // Verificar se já está conectado ao carregar
  useEffect(() => {
    checkIfWalletIsConnected();
  }, []);

  const checkIfWalletIsConnected = async () => {
    try {
      // Verificar MetaMask
      if (window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          await connectWithProvider(window.ethereum, 'MetaMask');
        }
      }
      // Verificar OKX
      else if (window.okxwallet) {
        const accounts = await window.okxwallet.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          await connectWithProvider(window.okxwallet, 'OKX Wallet');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar wallet:', error);
    }
  };

  // Conectar com provider específico
  const connectWithProvider = async (walletProvider, walletName) => {
    try {
      setLoading(true);
      
      console.log(`🔗 WalletContext: Conectando com ${walletName}`);
      console.log(`Provider recebido:`, walletProvider);
      console.log(`Provider.isMetaMask:`, walletProvider?.isMetaMask);
      console.log(`Provider.isOKExWallet:`, walletProvider?.isOKExWallet);
      console.log(`Provider === window.okxwallet:`, walletProvider === window.okxwallet);
      
      // VALIDAÇÃO: Garantir que estamos usando o provider correto
      if (!walletProvider) {
        throw new Error('Provider não fornecido');
      }
      
      // CRÍTICO: Usar o provider DIRETO passado, NÃO window.ethereum
      // Chamar request() diretamente no provider para garantir que estamos usando a carteira correta
      console.log(`📞 Solicitando acesso à carteira usando provider específico...`);
      
      let accounts;
      if (walletProvider.request) {
        // Usar request() diretamente - isso força o uso do provider específico
        accounts = await walletProvider.request({ method: 'eth_requestAccounts' });
      } else {
        throw new Error('Provider não suporta o método request()');
      }
      
      if (!accounts || accounts.length === 0) {
        throw new Error('Nenhuma conta encontrada. Por favor, autorize o acesso na sua carteira.');
      }
      
      const address = accounts[0];
      console.log(`✅ Endereço obtido: ${address}`);
      
      // Criar o provider do ethers para uso posterior (mas usando o walletProvider correto)
      const provider = new BrowserProvider(walletProvider);
      
      // Verificar se o endereço corresponde (validação adicional)
      const signer = await provider.getSigner();
      const verifiedAddress = await signer.getAddress();
      
      if (verifiedAddress.toLowerCase() !== address.toLowerCase()) {
        console.warn(`⚠️ Aviso: Endereços não correspondem exatamente`);
        console.warn(`Request: ${address}, Signer: ${verifiedAddress}`);
      }
      
      console.log(`✅ Endereço verificado: ${verifiedAddress}`);
      
      setWalletAddress(verifiedAddress);
      setIsConnected(true);
      setProvider(provider);
      setWalletType(walletName);
      
      // Verificar se é admin
      const adminStatus = ADMIN_ADDRESSES.includes(verifiedAddress.toLowerCase());
      setIsAdmin(adminStatus);
      
      // Para desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        setIsAdmin(true);
        console.log('🔧 Modo desenvolvimento: Admin habilitado');
      }
      
      console.log(`✅ ${walletName} conectada com sucesso:`, verifiedAddress);
      console.log('👤 É admin?', adminStatus);
      
      // Adicionar listeners ao provider original (não ao BrowserProvider)
      if (walletProvider.on) {
        walletProvider.on('accountsChanged', handleAccountsChanged);
        walletProvider.on('chainChanged', handleChainChanged);
      }
      
      return true;
    } catch (error) {
      console.error('❌ Erro ao conectar wallet:', error);
      console.error('Erro completo:', error.message, error.stack);
      throw error; // Re-throw para que o modal possa tratar
    } finally {
      setLoading(false);
    }
  };

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      const newAddress = accounts[0];
      setWalletAddress(newAddress);
      setIsAdmin(ADMIN_ADDRESSES.includes(newAddress.toLowerCase()));
    }
  };

  const handleChainChanged = () => {
    window.location.reload();
  };

  const disconnectWallet = () => {
    setWalletAddress('');
    setIsConnected(false);
    setIsAdmin(false);
    setProvider(null);
    setWalletType('');
    
    // Remover listeners
    if (window.ethereum?.removeListener) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    }
    
    console.log('👋 Wallet desconectada');
  };

  return (
    <WalletContext.Provider value={{
      walletAddress,
      isConnected,
      isAdmin,
      provider,
      loading,
      walletType,
      connectWithProvider,
      disconnectWallet
    }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet deve ser usado dentro de WalletProvider');
  }
  return context;
};
